import { cx } from "@emotion/css";
import type React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ExamAnswers } from "src/components/Exam/Answers/AnswersTypes";
import { isMultiAnswersExam } from "src/helpers/examHelpers";
import { getExam, useSetExcludedAnswer } from "src/hooks/useExam";
import ExcludedIcon from "src/images/Icons/newIcons/exam/answers/excluded.svg";
import { ExamState, ExamStatus } from "src/types/enums";
import isMobile from "src/helpers/isMobile";
import { IconButton } from "../../Default/LekUI/Button";
import { Tooltip } from "../../Default/LekUI/Tooltip";

interface ExamQuestion {
	id: string | number;
	answer: string | null;
	excludedAnswers: Array<{ answer: string }>;
	answers: Record<string, string>;
	correctAnswer: string;
	isCanceled: boolean;
}

interface SetAnswerPayload {
	questionId: string | number;
	answer: string;
	isCorrectAnswer: boolean;
	isAnnulledQuestion: boolean;
}

interface ExamAnswersOptionsProps {
	question: ExamQuestion;
	setAnswer: (payload: SetAnswerPayload) => Promise<void>;
}

export function ExamAnswersOptions({ question, setAnswer }: ExamAnswersOptionsProps) {
	const { t } = useTranslation();
	const params = useParams<{ id: string }>();
	const exam = getExam(params.id);
	const isPaused = exam.state === ExamState.PAUSED;
	const isFinished = exam.status === ExamStatus.FINISHED;

	const { mutateAsync: setExcludedAnswer } = useSetExcludedAnswer() as unknown as { mutateAsync: (payload: { answer: string; questionId: string | number; examId: string }) => Promise<void> };
	const setExamExcludedAnswer = async (answer: string) => {
		await setExcludedAnswer({
			answer,
			questionId: question.id,
			examId: exam.id,
		});
	};

	const isAnswerExcluded = (letter: string): boolean => {
		const answer = letter.toUpperCase();

		if (
			question.answer === answer ||
			(!isMultiAnswersExam(exam.type) && question.answer)
		) {
			return false;
		}

		return question.excludedAnswers.some(
			(excludedAnswer) => excludedAnswer.answer === answer,
		);
	};

	const renderAdditionalAction = (letter: string): React.ReactNode => {
		const answer = letter.toUpperCase();
		const isExcluded = isAnswerExcluded(letter);
		if (
			question.answer === answer ||
			(!isMultiAnswersExam(exam.type) && question.answer) ||
			isFinished
		) {
			return null;
		}

		return (
			<Tooltip title={t("tooltips.excludedAnswer")}>
				<IconButton
					className={cx("square-40", { "icon-button--not-active": isExcluded, "menu-icon-button": isMobile() })}
					onClick={async (e: React.MouseEvent) => {
						e.stopPropagation();
						e.preventDefault();
						if (!isPaused) {
							await setExamExcludedAnswer(answer);
						}
					}}
				>
					<ExcludedIcon className="icon-normal" />
				</IconButton>
			</Tooltip>
		);
	};

	return (
		<ExamAnswers
			question={question}
			setAnswer={setAnswer}
			examStatus={exam.status}
			allowedKeysPress
			setExcludedAnswer={setExamExcludedAnswer}
			renderAdditionalAction={renderAdditionalAction}
			isDisabled={isAnswerExcluded}
			shouldShowAnswer={!isMultiAnswersExam(exam.type)}
			examType={exam.type}
		/>
	);
}
